/**
 * Adapts phreatic per-well water metrics into the AnalyticalTimeseriesResponse
 * shape the existing SankeyDiagram consumes — so the phreatic water-level Sankey
 * reuses the same chart as the polygon analyte Sankey, with no new chart code.
 *
 * Design: the third ("Water Levels") column is RANGE BUCKETS, not raw values.
 * Each metric (saturated thickness, depth-to-water, GW elevation) is binned into
 * ranges (e.g. "Sat 40–60 ft"), and every well plugs into the bucket its value
 * falls in. Each well→bucket link counts as 1, so a bucket's size = how many
 * wells land in that range (a histogram drawn as a Sankey). This also sidesteps
 * the scale mismatch between metrics, since flows are counts, not magnitudes.
 */
import type {
  PhreaticWellLevelRow,
  AnalyticalTimeseriesResponse,
  AnalyticalTimeseriesAnalyte,
} from '@/api/dataVault';

const SNAPSHOT_DATE = '2024-01-01';
// Fixed-width ranges (ft): 0–20, 20–40, 40–60, … Each well plugs into the
// bucket its value falls in, per metric. Width is uniform across metrics per
// the spec; for a site-specific snip the local range keeps the bucket count
// modest. (GW elevation can span more ground — widen here if it gets busy.)
const BUCKET_WIDTH = 20;

const METRICS: { key: keyof PhreaticWellLevelRow; prefix: string }[] = [
  { key: 'sat_thickness_ft', prefix: 'Sat' },
  { key: 'dtw_ft', prefix: 'DTW' },
  { key: 'gw_elevation_ft', prefix: 'GW Elev' },
];

// Each well→bucket "measurement" is a single unit (count of 1).
function unitMeasurement(): AnalyticalTimeseriesAnalyte {
  return {
    compound: '',
    mcl: null,
    measurements: [
      {
        date: SNAPSHOT_DATE,
        concentration: 1,
        concentration_ugl: 1,
        is_non_detect: false,
        detection_limit: null,
      },
    ],
  };
}

// Fixed 20-ft bucket the value falls in, e.g. 45 → "Sat 40–60 ft".
function bucketLabel(prefix: string, v: number | null): string | null {
  if (v == null || !Number.isFinite(v)) return null;
  const lo = Math.floor(v / BUCKET_WIDTH) * BUCKET_WIDTH;
  return `${prefix} ${lo}–${lo + BUCKET_WIDTH} ft`;
}

export function phreaticLevelsToTimeseries(
  rows: PhreaticWellLevelRow[],
): AnalyticalTimeseriesResponse {
  return {
    wells: rows.map((r) => {
      const analytes: Record<string, AnalyticalTimeseriesAnalyte> = {};
      for (const m of METRICS) {
        const label = bucketLabel(m.prefix, r[m.key] as number | null);
        if (label) {
          const a = unitMeasurement();
          a.compound = label;
          analytes[label] = a;
        }
      }
      return {
        well_id: r.well_id,
        well_name: r.well_id,
        site_name: r.site_name,
        analytes,
      };
    }),
  };
}
